"use client";

import React from "react";
import { Mail, Phone, MessageCircle, MapPin, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SHARED_DATA = {
  email: "jk.floorsremoval@gmail.com",
  phone: "+1 610-549-8900",
  address: "Sacramento, CA 95814",
  whatsapp: "+16105498900"
};
export default function ConnectWithOurExperts() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="18291" id="claim-your-free-quote" ref={ref} className={`w-full py-20 bg-background text-foreground transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-8">
        
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-default font-extrabold text-foreground uppercase mb-4 tracking-tighter">
            GET A FREE QUOTE
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Expert Outdoor Assembly, Furniture Assembly, Floor & Carpet Removal, Deep Carpet Cleaning, and Wood Floor Treatment in Sacramento — Professional service done right the first time.
          </p>
        </div>

        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          
          <div className="flex flex-col items-center text-center group">
            <Link to={`mailto:${SHARED_DATA.email}`} className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-full transition-transform duration-300 group-hover:scale-110 bg-accent/50">
                <Mail className="size-12 text-primary" strokeWidth={1.5} />
              </div>
              <span className="text-primary font-default font-bold uppercase tracking-wider text-lg">
                EMAIL US
              </span>
            </Link>
          </div>

          
          <div className="flex flex-col items-center text-center group">
            <Link to={`tel:${SHARED_DATA.phone.replace(/\s+/g, "")}`} className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-full transition-transform duration-300 group-hover:scale-110 bg-accent/50">
                <Phone className="size-12 text-primary" strokeWidth={1.5} />
              </div>
              <span className="text-primary font-default font-bold uppercase tracking-wider text-lg">
                CALL NOW
              </span>
            </Link>
          </div>

          
          <div className="flex flex-col items-center text-center group">
            <Link to={`https://wa.me/${SHARED_DATA.whatsapp.replace(/[^0-9]/g, "")}`} newTab className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-full transition-transform duration-300 group-hover:scale-110 bg-accent/50">
                <MessageCircle className="size-12 text-primary" strokeWidth={1.5} />
              </div>
              <span className="text-primary font-default font-bold uppercase tracking-wider text-lg">
                WHATSAPP
              </span>
            </Link>
          </div>

          
          <div className="flex flex-col items-center text-center group">
            <Link to="https://maps.google.com" newTab className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-full transition-transform duration-300 group-hover:scale-110 bg-accent/50">
                <MapPin className="size-12 text-primary" strokeWidth={1.5} />
              </div>
              <span className="text-primary font-default font-bold uppercase tracking-wider text-lg">
                OUR AREA
              </span>
            </Link>
          </div>
        </div>

        
        <div className="text-center mb-16">
          <p className="text-lg text-muted-foreground font-default italic">
            "We treat your home with the professional care it deserves."
          </p>
        </div>

        
        <div className="max-w-3xl mx-auto">
          <Card className="border border-border shadow-xl bg-card/80 backdrop-blur-sm">
            <CardContent className="p-8 md:p-12">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-default font-extrabold text-secondary uppercase mb-2 tracking-tight">
                  Claim Your Free Quote
                </h3>
                <p className="text-muted-foreground font-default">
                  Fill out the form below and we'll get back to you shortly with a professional estimate.
                </p>
              </div>

              <FormProvider formKey="free_quote_form_home" formId="free_quote_form_home" sectionName="connect-with-our-experts" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              interest: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              message: {
                required: true,
                minLength: 20,
                maxLength: 1000
              }
            }} submitText="Get My Free Quote" successMessage="Thank you! We've received your request and will contact you soon." errorMessage="Something went wrong. Please try calling us directly.">
                <ContactForm />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-accent/30 rounded-xl border border-primary/20">
        <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-2">
          <MessageCircle className="size-6" />
        </div>
        <h4 className="text-xl font-default font-bold text-foreground">Request Received!</h4>
        <p className="text-muted-foreground font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/20 text-primary hover:bg-accent hover:text-primary-foreground font-default">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="bg-background border-border focus:border-primary" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-default">
                Full name for the estimate
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-background border-border focus:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 610-549-8900" className="bg-background border-border focus:border-primary" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-default">
                Best number to reach you
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Zip Code</FormLabel>
              <FormControl>
                <Input placeholder="95814" className="bg-background border-border focus:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      
      <div className="mb-6">
        <FormField control={control} name="interest" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Service Needed</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background border-border focus:border-primary">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {["Outdoor Assembly (Gazebos, pergolas, playgrounds, sheds)", "Furniture Assembly (Beds, IKEA, Wayfair, Amazon furniture)", "Floor & Carpet Removal (Carpet removal, vinyl, laminate, hardwood, engineered wood removal and disposal)", "Deep Carpet Cleaning (Odor removal, deep sanitation, restore freshness)", "Wood Floor Treatment (Cleaning, polishing, restoration, NOT installation)", "Other"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      
      <div className="mb-8">
        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 20,
          message: "Details must be at least 20 characters"
        },
        maxLength: {
          value: 1000,
          message: "Message cannot exceed 1000 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Project Details</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about the project (e.g., room size, material type)..." className="min-h-[150px] resize-y bg-background border-border focus:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      
      {submitError && <div className="p-4 mb-6 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      
      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-lg uppercase tracking-wide rounded-md bg-gradient-to-r from-primary to-orange-600 text-primary-foreground font-default shadow-lg hover:shadow-xl transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : "Get My Free Quote"}
      </Button>
      <div className="mt-4 text-center">
        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1 font-default">
          <span className="inline-block size-3 bg-secondary/10 rounded-full flex items-center justify-center"></span>
          Your privacy is guaranteed.
        </p>
      </div>
    </Form>;
}